import { ClipboardList, Users, AlertTriangle, Loader2, Menu, Type, Briefcase, User, LogOut, UserCog, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import VoiceCallButton from "@/components/VoiceCallButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FONT_SIZES = [
  { label: "A", value: "small", size: "14px" },
  { label: "A", value: "normal", size: "16px" },
  { label: "A", value: "large", size: "19px" },
  { label: "A", value: "xlarge", size: "22px" },
];

export default function Header({
  orderCount,
  onShowOrders,
  onShowFamily,
  onShowScamAlerts,
  onShowMCP,
  sessionId,
  onSOSTriggered,
  profile,
  onLoginClick,
  onLogout,
  onEditProfileClick,
  onMyProfileClick,
}) {
  const [sosLoading, setSOSLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [sosConfirm, setSOSConfirm] = useState(false);
  const [fontSize, setFontSize] = useState(() => localStorage.getItem("elderraFontSize") || "normal");

  useEffect(() => {
    const size = FONT_SIZES.find(f => f.value === fontSize)?.size || "16px";
    document.documentElement.style.setProperty("--elderra-font-size", size);
    localStorage.setItem("elderraFontSize", fontSize);
  }, [fontSize]);

  const handleSOSClick = () => {
    if (!sessionId) return;
    setSOSConfirm(true);
  };

  const handleSOSConfirm = async () => {
    setSOSConfirm(false);
    setSOSLoading(true);
    // Try to get current location for the SOS alert
    let lat = null, lng = null;
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false, timeout: 5000, maximumAge: 60000,
        });
      });
      lat = pos.coords.latitude;
      lng = pos.coords.longitude;
    } catch { /* continue without location */ }

    try {
      if (onSOSTriggered) {
        onSOSTriggered({ lat, lng });
        toast.success("SOS sent. Help is being alerted.");
        return;
      }
      const params = new URLSearchParams({ session_id: sessionId });
      if (lat !== null) {
        params.append("lat", lat.toString());
        params.append("lng", lng.toString());
      }
      const res = await axios.post(`${API}/sos/trigger`, params);
      toast.success(`SOS sent to ${res.data.notified?.length || 0} family member(s)!`);
      if (onSOSTriggered && res.data.notification) {
        onSOSTriggered(res.data.notification);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.includes("No family members")) {
        toast.error("No family members saved. Add a family contact first.", { action: { label: "Add", onClick: onShowFamily } });
      } else {
        toast.error("SOS could not be sent. Please try again.");
      }
    } finally {
      setSOSLoading(false);
    }
  };

  return (
    <>
      <header
        className="flex items-center justify-between px-3 py-3 md:px-6 md:py-4 shrink-0"
        style={{ backgroundColor: "#2D2766" }}
        data-testid="app-header"
      >
        {/* Left: hamburger + avatar + name */}
        <div className="flex items-center gap-1.5 md:gap-3 min-w-0 flex-1 mr-2">
          <button
            onClick={() => setMenuOpen(true)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors shrink-0"
            aria-label="Open menu"
            data-testid="hamburger-menu-btn"
          >
            <Menu className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </button>
          <div
            className="shrink-0 rounded-full bg-white p-1.5 shadow-sm ring-1 ring-white/30 flex items-center justify-center"
            data-testid="bot-avatar-frame"
          >
            <img
              src="/elderra-avatar.png"
              alt="Elderra Agent"
              className="w-8 h-8 md:w-10 md:h-10 rounded-full object-contain"
              data-testid="bot-avatar"
            />
          </div>
          <div className="min-w-0">
            <h1
              className="text-white font-semibold text-base md:text-xl leading-tight truncate"
              data-testid="app-title"
            >
              Elderra Agent
            </h1>
            <p className="text-purple-200 text-xs md:text-sm truncate" data-testid="header-subtitle">
              {profile ? `Namaste, ${profile.name?.split(" ")[0] || "friend"}` : "Online"}
            </p>
          </div>
        </div>

        {/* Right: SOS + Orders (priority) + Login/Logout — Family Contacts lives in the hamburger menu */}
        <div className="flex items-center gap-1 md:gap-3 shrink-0">
          {/* Login / account */}
          {profile ? (
            <>
              <button
                onClick={onEditProfileClick}
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                title="My profile"
                data-testid="edit-profile-btn"
              >
                <UserCog className="w-4 h-4" />
                <span className="hidden md:inline">My Profile</span>
              </button>
              <button
                onClick={onLogout}
                className="p-2 sm:px-3 sm:py-2 flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                title="Log out"
                data-testid="user-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Log out</span>
              </button>
            </>
          ) : (
            <button
              onClick={onLoginClick}
              className="p-2 sm:px-3 sm:py-2 flex items-center gap-1.5 rounded-full bg-white text-[#2D2766] text-sm font-semibold hover:bg-purple-50 transition-colors"
              title="Log in to load your profile"
              data-testid="login-btn"
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Login</span>
            </button>
          )}

          {/* Voice Call */}
          <VoiceCallButton sessionId={sessionId} />

          {/* Orders — prioritized on mobile over Family Contacts (kept in hamburger menu) */}
          <button
            onClick={onShowOrders}
            className="relative p-2 md:p-2.5 rounded-full hover:bg-white/10 transition-colors"
            aria-label="View orders"
            data-testid="orders-btn"
          >
            <ClipboardList className="w-5 h-5 md:w-6 md:h-6 text-white" />
            {orderCount > 0 && (
              <Badge
                className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 min-w-[20px] h-5 flex items-center justify-center border-0 order-badge-pulse"
                data-testid="order-count-badge"
              >
                {orderCount}
              </Badge>
            )}
          </button>

          {/* SOS Button */}
          <button
            onClick={handleSOSClick}
            disabled={sosLoading}
            className="flex items-center gap-1 px-2.5 py-2 md:gap-1.5 md:px-4 md:py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs md:text-base font-bold transition-all active:scale-95 shadow-lg shadow-red-600/30 disabled:opacity-60 shrink-0"
            data-testid="sos-btn"
            title="Send emergency alert to family"
            aria-label="Send emergency SOS alert to family"
          >
            {sosLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <AlertTriangle className="w-4 h-4 md:w-5 md:h-5" />
            )}
            SOS
          </button>
        </div>
      </header>

      {/* SOS Confirmation Dialog */}
      {sosConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="sos-confirm-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSOSConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 mx-4 max-w-sm w-full text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Send Emergency Alert?</h3>
            <p className="text-sm text-gray-600 mb-5">This will notify ALL your saved family members about an emergency.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setSOSConfirm(false)}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
                data-testid="sos-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSOSConfirm}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 transition-colors"
                data-testid="sos-confirm-btn"
              >
                YES, SEND NOW
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hamburger Sidebar */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="sidebar-menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <div className="relative w-72 max-w-[80vw] bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-left duration-200">
            {/* Sidebar Header */}
            <div className="px-5 py-5 flex items-center gap-3" style={{ backgroundColor: "#2D2766" }}>
              <div className="rounded-full bg-white p-1.5 shadow-sm ring-1 ring-white/30 shrink-0 flex items-center justify-center">
                <img src="/elderra-avatar.png" alt="Elderra" className="w-10 h-10 rounded-full object-contain" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-lg">Elderra Agent</h2>
                <p className="text-purple-200 text-xs">Your caring digital assistant</p>
              </div>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 py-2">
              <SidebarItem
                icon={<UserCog className="w-5 h-5" />}
                label="My Profile"
                sub="Your saved details — name, address, blood group & more"
                testId="menu-my-profile"
                onClick={() => { setMenuOpen(false); (onMyProfileClick || onEditProfileClick)?.(); }}
              />
              <SidebarItem
                icon={<AlertTriangle className="w-5 h-5" />}
                label="Fraud Awareness"
                sub="Scam alerts & safety tips"
                testId="menu-fraud-awareness"
                onClick={() => { setMenuOpen(false); onShowScamAlerts?.(); }}
                accent
              />
              <SidebarItem
                icon={<ClipboardList className="w-5 h-5" />}
                label="My Orders"
                sub="View order history"
                testId="menu-orders"
                onClick={() => { setMenuOpen(false); onShowOrders(); }}
              />
              <SidebarItem
                icon={<Users className="w-5 h-5" />}
                label="Family Contacts"
                sub="Manage emergency contacts"
                testId="menu-family"
                onClick={() => { setMenuOpen(false); onShowFamily(); }}
              />
              <div className="border-t my-2 mx-4" />
              <SidebarItem
                icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>}
                label="Connected Apps"
                sub="Swiggy, Uber & more"
                testId="menu-mcp"
                onClick={() => { setMenuOpen(false); onShowMCP?.(); }}
              />
              <div className="border-t my-2 mx-4" />
              <SidebarItem
                icon={<Briefcase className="w-5 h-5" />}
                label="Service Provider Signup"
                sub="Are you a pharmacy, nurse, doctor or cab? Join us"
                testId="menu-vendor-signup"
                onClick={() => { setMenuOpen(false); window.open("/vendor", "_blank", "noopener,noreferrer"); }}
              />
              <div className="border-t my-2 mx-4" />
              <SidebarItem
                icon={<Info className="w-5 h-5" />}
                label="About Us"
                sub="Who we are & policies"
                testId="menu-about-us"
                onClick={() => { setMenuOpen(false); setShowAbout(true); }}
              />
            </nav>

            {/* Text Size Control */}
            <div className="px-5 py-3 border-t" data-testid="text-size-control">
              <div className="flex items-center gap-2 mb-2">
                <Type className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600">Text Size</span>
              </div>
              <div className="flex gap-1.5">
                {FONT_SIZES.map((f, i) => (
                  <button
                    key={f.value}
                    onClick={() => setFontSize(f.value)}
                    className={`flex-1 py-1.5 rounded-lg text-center font-semibold transition-colors ${
                      fontSize === f.value
                        ? "bg-[#2D2766] text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    style={{ fontSize: `${12 + i * 3}px` }}
                    data-testid={`font-size-${f.value}`}
                    aria-label={`Set text size to ${f.value}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t bg-gray-50">
              <p className="text-xs text-gray-400">Helpline: 14567 (Elderline)</p>
              <p className="text-xs text-gray-400 mt-0.5">Cyber Crime: 1930</p>
            </div>
          </div>
        </div>
      )}

      {/* About Us modal */}
      {showAbout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="about-us-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAbout(false)} />
          <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 flex items-center gap-3" style={{ backgroundColor: "#2D2766" }}>
              <Info className="w-5 h-5 text-white" />
              <h3 className="text-white font-semibold text-lg">About Us</h3>
              <button
                onClick={() => setShowAbout(false)}
                className="ml-auto text-white/80 hover:text-white text-xl leading-none"
                data-testid="about-us-close"
                aria-label="Close"
              >×</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-800 leading-relaxed" data-testid="about-us-text">
                Elderra Agent is an initiative by{" "}
                <a
                  href="https://elderra.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2D2766] font-medium underline underline-offset-2"
                  data-testid="about-us-elderra-link"
                >Elderra.io</a>
                {" "}— empowering India's senior citizens with digital literacy, in-home DigiMitra support, and a WhatsApp-based assistant that helps them order essentials, book services, and stay safe from online scams.
              </p>
              <div className="border-t pt-4">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Policies</p>
                <ul className="space-y-2">
                  {[
                    { label: "Terms of Use", href: "https://elderra.io/terms-of-use", testId: "about-us-terms" },
                    { label: "Privacy Policy", href: "https://elderra.io/privacy-policy", testId: "about-us-privacy" },
                    { label: "Data Deletion Request", href: "https://elderra.io/data-deletion-request", testId: "about-us-data-deletion" },
                    { label: "Refund Policy", href: "https://elderra.io/refund-policy", testId: "about-us-refund" },
                    { label: "FAQs", href: "https://elderra.io/faqs", testId: "about-us-faqs" },
                  ].map((l) => (
                    <li key={l.testId}>
                      <a
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between text-sm text-gray-800 hover:text-[#2D2766] hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors"
                        data-testid={l.testId}
                      >
                        <span>{l.label}</span>
                        <span aria-hidden className="text-gray-400">↗</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SidebarItem({ icon, label, sub, testId, onClick, accent }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors ${accent ? "bg-red-50 hover:bg-red-100" : ""}`}
      data-testid={testId}
    >
      <div className={`${accent ? "text-red-600" : "text-[#2D2766]"}`}>{icon}</div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm ${accent ? "text-red-800" : "text-gray-900"}`}>{label}</p>
        {sub && <p className={`text-xs mt-0.5 ${accent ? "text-red-500" : "text-gray-500"}`}>{sub}</p>}
      </div>
    </button>
  );
}
